import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue';
import {
  AppsEnum,
  CustomFormDataEntitiesEnum,
  CustomFormsClaimFeatures,
  CustomFormsFeatures,
  CustomFormsFeaturesData,
  CustomFormsGateManagementFeatures,
  getReschedulesCount,
  getReschedulesString,
  isRescheduleAllowed
} from '@nova/core';
import { useAppointmentHelpers, useEventHub, useSockets } from '@/composables/index';
import { useLoadTypeDirectionDisplay } from '@/composables/useLoadTypeDirectionDisplay';
import { useStore } from 'vuex';
import { useFlag } from '@loadsmart/vue-feature-flags';
import customFormsService from '@satellite/services/custom-forms-service.js';

export const appointmentDetailsHelpersProps = {
  appointment: {
    type: Object,
    required: true
  },
  shouldEditStatus: {
    type: Boolean,
    required: false,
    default: false
  },
  shouldDisplayCancelButton: {
    type: Boolean,
    required: false,
    default: true
  },
  appointmentWarehouse: {
    type: Object,
    required: true
  },
  readOnly: {
    type: Boolean,
    required: false
  }
};

export default function useAppointmentDetailsHelpers(
  appointment = {},
  appointmentWarehouse = {},
  readOnly = false
) {
  const sockets = useSockets();
  const eventHub = useEventHub();
  const store = useStore();
  const { getDirectionTitle } = useLoadTypeDirectionDisplay();
  const checkInPerLoadTypeFlag = useFlag('enable-check-in-per-load-type');
  const isCheckInPerLoadTypeEnabled = computed(() => Boolean(checkInPerLoadTypeFlag.value.enabled));
  const rolePermissions = store.state.Auth.rolePermissions;
  const showRescheduleDialog = ref(false);
  const loading = ref(true);
  const tags = ref(appointment.tags);
  const lockTagEditing = ref(false);
  const warehouseTriggers = ref([]);
  const assetVisitFormData = ref([]);
  const appointmentHelpers = useAppointmentHelpers(appointment);
  const appointmentTableRows = ref({
    dockName: {
      label: 'Dock Name',
      value: `${appointment.readableDockName ?? appointment.dock?.name ?? 'Dock'}${
        appointment.dock.isActive === false ? ' (Inactive)' : ''
      }`,
      tdClass: 'pb-4',
      isHidden: false
    },
    loadType: {
      label: 'Load Type',
      value: '',
      tdClass: 'pb-4',
      isHidden: false
    },
    refNumber: {
      label: 'Reference Number',
      value: appointment.refNumber || '----',
      tdClass: 'pb-4',
      isHidden: false
    }
  });

  const canReschedule = computed(() => {
    return (
      rolePermissions.canUpdateAppointment &&
      isRescheduleAllowed(appointment.status, appointment.type) &&
      !readOnly
    );
  });

  const reschedulesCount = computed(() => {
    return getReschedulesCount(appointment);
  });

  const reschedulesString = computed(() => {
    return getReschedulesString(reschedulesCount.value);
  });

  const gateManagementTriggers = computed(() => {
    const triggers =
      warehouseTriggers.value?.filter(trigger => {
        const isGateManagementTrigger =
          CustomFormsGateManagementFeatures.includes(trigger.feature) &&
          [CustomFormDataEntitiesEnum.appointment, CustomFormDataEntitiesEnum.assetVisit].includes(
            trigger.dataEntityName
          );
        if (!isGateManagementTrigger) {
          return false;
        }

        // Show only driver check-out in the GateYard tab, not warehouse check-out
        const isCheckOut = [CustomFormsFeatures?.CheckOut, 'check-out'].includes(trigger.feature);
        if (isCheckOut && trigger.app !== AppsEnum.Driver) {
          return false;
        }

        return true;
      }) || [];

    return triggers;
  });

  const assetDetailsTriggers = computed(() => {
    return (
      warehouseTriggers.value?.filter(
        t => t.dataEntityName === CustomFormDataEntitiesEnum.assetContainer
      ) || []
    );
  });

  const claimSupportTriggers = computed(() => {
    return warehouseTriggers.value?.filter(t => CustomFormsClaimFeatures.includes(t.feature)) || [];
  });

  const featureData = feature => {
    return CustomFormsFeaturesData[feature] || {};
  };

  const initializeRowValues = () => {
    const refNumSettings = store.getters['Settings/refNumSettings'](appointmentWarehouse);
    appointmentTableRows.value.loadType.value = store.getters['LoadTypes/loadTypeName']({
      loadType: appointment.loadType,
      directionText: getDirectionTitle(appointment.loadType)
    });
    appointmentTableRows.value.refNumber.label = refNumSettings.displayName;
    appointmentTableRows.value.refNumber.isHidden = !refNumSettings.isVisible;
    appointmentTableRows.value.refNumber.value = appointment.refNumber;
    appointmentTableRows.value.dockName.value = appointment.dock.name;
  };

  const updateAppointmentTags = async () => {
    lockTagEditing.value = true;
    await window.axios
      .patch(`appointment/${appointment.id}`, {
        tags: tags.value
      })
      .finally(() => (lockTagEditing.value = false));
  };

  // runs only check in per loadType flag is on
  const getLoadTypeTriggers = async () => {
    const loadTypeId = appointment.loadTypeId ?? appointment.loadType?.id;

    if (!isCheckInPerLoadTypeEnabled.value || !loadTypeId || !appointmentWarehouse.id) {
      return [];
    }

    try {
      return await customFormsService.getLoadTypesTriggers([loadTypeId], {
        warehouseId: appointmentWarehouse.id
      });
    } catch (err) {
      console.error(
        `Failed to fetch check in per load type triggers for load type ${loadTypeId} at warehouse ${appointmentWarehouse.id}`,
        err
      );
      return [];
    }
  };

  const setWarehouseTriggers = async () => {
    const [warehouseScoped, loadTypeScoped] = await Promise.all([
      customFormsService.getWarehouseTriggers(appointmentWarehouse.id),
      getLoadTypeTriggers()
    ]);

    warehouseTriggers.value = [...warehouseScoped, ...loadTypeScoped];
  };

  const getAssetVisitFormData = async () => {
    if (appointment.assetVisit) {
      try {
        return customFormsService.getCustomFormData(appointment.assetVisit.id);
      } catch (_) {
        return [];
      }
    }
  };

  onMounted(async () => {
    initializeRowValues();
    appointmentHelpers.setCustomFieldsRows();
    const cachedTriggers = store.state.Calendar.selectedWarehouseTriggers;
    const isCacheForThisWarehouse =
      store.state.Calendar.selectedWarehouse?.id === appointmentWarehouse.id;
    if (isCacheForThisWarehouse && cachedTriggers?.length) {
      warehouseTriggers.value = [...cachedTriggers, ...(await getLoadTypeTriggers())];
    } else {
      await setWarehouseTriggers();
    }
    assetVisitFormData.value = await getAssetVisitFormData();
    loading.value = false;
    eventHub.$on(`update-Trigger`, setWarehouseTriggers);
    eventHub.$on(`create-FormField`, setWarehouseTriggers);
  });

  onBeforeUnmount(() => {
    Object.keys(sockets.actions).map(action => {
      eventHub.$off(`${action}-Trigger`, setWarehouseTriggers);
      eventHub.$off(`${action}-FormField`, setWarehouseTriggers);
    });
  });

  watch(tags, async newTags => {
    if (JSON.stringify(newTags) !== JSON.stringify(appointment.tags)) {
      await updateAppointmentTags();
    }
  });

  watch(
    () => getDirectionTitle(appointment.loadType),
    () => initializeRowValues()
  );

  watch(
    () => appointment,
    newAppointment => {
      initializeRowValues();
      appointmentHelpers.setCustomFieldsRows();
      tags.value = newAppointment.tags;
    }
  );

  return {
    showRescheduleDialog,
    loading,
    tags,
    lockTagEditing,
    warehouseTriggers,
    assetVisitFormData,
    appointmentTableRows,
    canReschedule,
    reschedulesCount,
    reschedulesString,
    gateManagementTriggers,
    assetDetailsTriggers,
    claimSupportTriggers,
    featureData,
    initializeRowValues,
    updateAppointmentTags,
    setWarehouseTriggers,
    getLoadTypeTriggers,
    getAssetVisitFormData
  };
}
